/**
 * NotificationBell component for PeoplePulse HR Management System.
 * Header notification bell with unread count and real-time updates.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, BellRing } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import NotificationCenter from './NotificationCenter';
import { apiService } from '../services/apiService';

const NotificationBell = ({ className = '' }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const websocketRef = useRef(null);

  // Load initial unread count
  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await apiService.get('/api/notifications/unread-count');
      setUnreadCount(response.unread_count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  }, []);

  // Initialize WebSocket connection for real-time updates
  const initializeWebSocket = useCallback(() => {
    try {
      // Get user ID from auth context or token
      const token = localStorage.getItem('authToken');
      if (!token) return;

      // Decode token to get user ID with error handling
      let userId;
      try {
        if (typeof token === 'string' && token.split('.').length === 3) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.sub || payload.user_id;
        }
      } catch (decodeError) {
        console.error('Failed to decode JWT token:', decodeError);
        return;
      }

      if (!userId) return;

      // Create WebSocket connection
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/api/notifications/ws/${userId}?token=${encodeURIComponent(token)}`;
      
      const ws = new WebSocket(wsUrl);
      websocketRef.current = ws;

      // Send ping every 30 seconds to keep connection alive
      let pingInterval;

      ws.onopen = () => {
        console.log('Notification WebSocket connected');
        setIsConnected(true);
        
        pingInterval = setInterval(() => {
          if (websocketRef.current?.readyState === WebSocket.OPEN) {
            websocketRef.current.send('ping');
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          handleWebSocketMessage(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        console.log('Notification WebSocket disconnected');
        setIsConnected(false);
        websocketRef.current = null;
        
        if (pingInterval) {
          clearInterval(pingInterval);
        }
        
        // Attempt to reconnect after 5 seconds
        setTimeout(() => {
          if (!websocketRef.current || websocketRef.current.readyState === WebSocket.CLOSED) {
            initializeWebSocket();
          }
        }, 5000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
      };

    } catch (error) {
      console.error('Error initializing WebSocket:', error);
    }
  }, []);

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = (message) => {
    switch (message.type) {
      case 'notification':
        // New notification received
        setUnreadCount(prev => prev + 1);
        
        // Show toast for high/urgent priority notifications
        if (message.data.priority === 'high' || message.data.priority === 'urgent') {
          toast({
            title: message.data.title,
            description: message.data.message,
            duration: 5000
          });
        }
        break;

      case 'notification_read':
        // Notification marked as read
        setUnreadCount(prev => Math.max(0, prev - 1));
        break;

      case 'notification_dismissed':
        // Notification dismissed
        setUnreadCount(prev => Math.max(0, prev - 1));
        break;

      case 'notifications_marked_read': {
        // Multiple notifications marked as read
        const count = Number(message.data.count);
        const validatedCount = Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
        setUnreadCount(prev => Math.max(0, prev - validatedCount));
        break;
      }

      case 'notification_deleted':
        // Notification deleted
        setUnreadCount(prev => Math.max(0, prev - 1));
        break;

      default:
        console.log('Unknown WebSocket message type:', message.type);
    }
  };

  // Initialize on mount
  useEffect(() => {
    loadUnreadCount();
    initializeWebSocket();

    // Listen for manual refresh events (e.g., after creating absence)
    const handleRefresh = () => {
      loadUnreadCount();
    };
    window.addEventListener('notifications:refresh', handleRefresh);

    // Cleanup on unmount
    return () => {
      if (websocketRef.current) {
        websocketRef.current.close();
      }
      window.removeEventListener('notifications:refresh', handleRefresh);
    };
  }, [loadUnreadCount, initializeWebSocket]);

  // Add polling fallback if WebSocket is not connected
  useEffect(() => {
    if (!isConnected) {
      const pollingInterval = setInterval(() => {
        loadUnreadCount();
      }, 30000); // Poll every 30 seconds if WebSocket is down

      return () => {
        clearInterval(pollingInterval);
      };
    }
  }, [isConnected, loadUnreadCount]);

  // Handle visibility change to refresh count when tab becomes active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadUnreadCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadUnreadCount]);

  const toggleNotificationCenter = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationCenterClose = () => {
    setIsOpen(false);
    // Refresh unread count when closing
    loadUnreadCount();
  };

  return (
    <>
      <div className={`relative ${className}`}>
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleNotificationCenter}
          className={`relative p-2 ${isOpen ? 'bg-gray-100' : ''}`}
          aria-label={`Notifications ${unreadCount > 0 ? `(${unreadCount} unread)` : ''}`}
        >
          {unreadCount > 0 ? (
            <BellRing className="w-5 h-5 text-blue-600" />
          ) : (
            <Bell className="w-5 h-5 text-gray-600" />
          )}
          
          {/* Unread Count Badge */}
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 p-0 text-xs flex items-center justify-center"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
          
          {/* Connection Status Indicator */}
          {!isConnected && (
            <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-orange-500 rounded-full" 
                 title="Reconnecting..." />
          )}
        </Button>

        {/* Pulse Animation for New Notifications */}
        {unreadCount > 0 && (
          <div className="absolute inset-0 rounded-full animate-pulse bg-blue-400 opacity-20 pointer-events-none" />
        )}
      </div>

      {/* Notification Center */}
      <NotificationCenter 
        isOpen={isOpen} 
        onClose={handleNotificationCenterClose}
      />
    </>
  );
};

export default NotificationBell;